import { InvokeCommand, LambdaClient } from '@aws-sdk/client-lambda';
import { PutObjectCommand, S3Client } from '@aws-sdk/client-s3';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, SelectorIcon, DocumentIcon, XIcon } from '@heroicons/react/solid';
import { isEmpty, isNil } from 'lodash';
import { Fragment, useEffect, useState } from 'react';
import { useFilePicker } from 'use-file-picker';

const contentTypeMap = new Map<string, string>([
  ['pdf', 'application/pdf'],
  ['png', 'image/png'],
  ['jpg', 'image/jpeg'],
  ['jpeg', 'image/jpeg'],
]);

export const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const years = [
  '2021',
  '2022',
  '2023',
  '2024',
  '2025',
  '2026',
  '2027',
  '2028',
  '2029',
];

interface FileAdderProps {
  s3Client: S3Client;
  lambdaClient: LambdaClient;
  getFiles: () => void;
}

export const FileAdder = (props: FileAdderProps) => {
  const [restrictedMap, setRestrictedMap] = useState(new Map<string, boolean>());
  const [sendEmailMap, setSendEmailMap] = useState(new Map<string, boolean>());
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [folderMonth, setFolderMonth] = useState<string>(months[0]);
  const [folderYear, setFolderYear] = useState<string>(years[0]);
  const [generalDocClicked, setGeneralDocClicked] = useState(true);
  const [openFileSelector, { filesContent, loading }] = useFilePicker({
    accept: ['.pdf', '.png', '.jpg', '.jpeg'],
    multiple: true,
    readAs: 'ArrayBuffer',
  });
  const [allSelectedFiles, setAllSelectedFiles] = useState<any[]>([]);

  useEffect(() => {
    if (filesContent.length > 0) {
      setAllSelectedFiles((prev) => {
        const existingNames = new Set(prev.map((f) => f.name));
        const newFiles = filesContent.filter((f) => !existingNames.has(f.name));
        return [...prev, ...newFiles];
      });
    }
  }, [filesContent]);

  useEffect(() => {
    for (const content of allSelectedFiles) {
      if (isNil(sendEmailMap.get(content.name))) {
        sendEmailMap.set(content.name, true);
      }
    }
    setSendEmailMap(new Map(sendEmailMap));
  }, [allSelectedFiles]);

  const addFile = () => {
    openFileSelector();
  };

  const removeSelectedFile = (fileName: string) => {
    setAllSelectedFiles((prev) => prev.filter((f) => f.name !== fileName));
  };

  const sendEmailNotification = async (files: string[]) => {
    await props.lambdaClient.send(
      new InvokeCommand({
        FunctionName: 'BirthdayEmailV2',
        Payload: new TextEncoder().encode(JSON.stringify({ files })),
      })
    );
  };

  const uploadFiles = async () => {
    setIsPageLoading(true);
    let success = true;
    const files = [];
    for (const content of allSelectedFiles) {
      // DECISIVE FIX: Remove " Meeting" suffix from folder creation
      const folderName = generalDocClicked ? 'General' : `${folderMonth} ${folderYear}`;
      if (sendEmailMap.get(content.name)) {
        files.push(`${folderName}/${content.name}${restrictedMap.get(content.name) ? '##RESTRICTED' : ''}`);
      }
      const command = new PutObjectCommand({
        Bucket: restrictedMap.get(content.name) ? 'protected-mitchellfc-docs-post-grad' : 'protected-mitchellfc-docs-everyone',
        Key: folderName + '/' + content.name,
        Body: content.content,
        ACL: 'bucket-owner-full-control',
        ContentType: contentTypeMap.get(content.name.split('.')[1]),
      });
      try {
        await props.s3Client.send(command);
      } catch (e) {
        success = false;
      }
    }
    if (success) {
      await sendEmailNotification(files);
      setUploadMessage('File(s) uploaded successfully');
    } else {
      setUploadMessage(
        'Error occured while uploading files.  Please try again and tell bobmitch1995@gmail.com if it does not work the second time.'
      );
    }
    props.getFiles();
    setIsPageLoading(false);
  };

  const onRestrictedClick = (fileName: string) => {
    let clicked = restrictedMap.get(fileName);
    if (isNil(clicked)) {
      clicked = false;
    }
    setRestrictedMap(new Map(restrictedMap.set(fileName, !clicked)));
  };

  const isRestrictedClicked = (fileName: string) => {
    if (isNil(restrictedMap.get(fileName))) {
      return false;
    }
    return restrictedMap.get(fileName);
  };

  const onSendEmailClick = (fileName: string) => {
    let clicked = sendEmailMap.get(fileName);
    if (isNil(clicked)) {
      clicked = false;
    }
    setSendEmailMap(new Map(sendEmailMap.set(fileName, !clicked)));
  };

  const isSendEmailClicked = (fileName: string) => {
    if (isNil(sendEmailMap.get(fileName))) {
      return false;
    }
    return sendEmailMap.get(fileName);
  };

  return (
    <>
      {(isPageLoading || loading) && (
        <div className="w-full h-full fixed block top-0 left-0 bg-slate-50/50 dark:bg-slate-900/50 backdrop-blur-sm z-[1000]">
          <div className="flex items-center justify-center h-full">
            <div className="w-16 h-16 border-b-4 border-blue-600 rounded-full animate-spin"></div>
          </div>
        </div>
      )}
      
      <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-xl text-sm text-blue-800 dark:text-blue-200 border border-blue-100 dark:border-blue-800/50 leading-relaxed">
        <span className="font-bold">Formatting Note:</span> Every file must either be an image or PDF (please convert PowerPoint, Excel, Word etc. to PDF). While PDFs are not truly immutable, they appear more final and are standardized for browser previewing.
      </div>

      <div className="mb-8">
        <button
          type="button"
          className="w-full py-8 px-4 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-2xl bg-slate-50 dark:bg-slate-800/50 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:border-blue-500 dark:hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 transition-all font-medium flex flex-col items-center justify-center gap-3 group"
          onClick={addFile}
        >
          <div className="p-3 bg-white dark:bg-slate-700 rounded-full shadow-sm group-hover:scale-110 transition-transform">
            <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
          </div>
          <span>Click to select files from your computer</span>
        </button>
      </div>

      {allSelectedFiles.length > 0 && (
        <div className="space-y-8 animate-in fade-in duration-500">
          <div>
            <h4 className="text-lg font-extrabold tracking-tight dark:text-white text-slate-900 mb-4">1. Select Destination Folder</h4>
            <div className="flex flex-col sm:flex-row gap-4 mb-5">
              <label className={`flex-1 flex items-center p-4 border rounded-xl cursor-pointer transition-all ${generalDocClicked ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-500' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/50'}`}>
                <input type="radio" className="sr-only" checked={generalDocClicked} onChange={() => setGeneralDocClicked(true)} />
                <div className={`w-5 h-5 rounded-full border flex items-center justify-center mr-3 shrink-0 ${generalDocClicked ? 'border-blue-500' : 'border-slate-300 dark:border-slate-600'}`}>
                  {generalDocClicked && <div className="w-2.5 h-2.5 bg-blue-500 rounded-full" />}
                </div>
                <div>
                  <div className="font-bold text-slate-900 dark:text-white">General Folder</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Place files in the main directory</div>
                </div>
              </label>
              <label className={`flex-1 flex items-center p-4 border rounded-xl cursor-pointer transition-all ${!generalDocClicked ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-500' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/50'}`}>
                <input type="radio" className="sr-only" checked={!generalDocClicked} onChange={() => setGeneralDocClicked(false)} />
                <div className={`w-5 h-5 rounded-full border flex items-center justify-center mr-3 shrink-0 ${!generalDocClicked ? 'border-blue-500' : 'border-slate-300 dark:border-slate-600'}`}>
                  {!generalDocClicked && <div className="w-2.5 h-2.5 bg-blue-500 rounded-full" />}
                </div>
                <div>
                  <div className="font-bold text-slate-900 dark:text-white">Dated Folder</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Organize files by month and year</div>
                </div>
              </label>
            </div>

            {!generalDocClicked && (
              <div className="grid grid-cols-2 gap-4 p-5 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
              <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Month</label>
                    <Listbox value={folderMonth} onChange={(value) => setFolderMonth(value)}>
                      <div className="relative mt-1">
                      <Listbox.Button className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all shadow-sm text-left">
                        <span className="block truncate">{folderMonth}</span>
                        <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <SelectorIcon className="w-5 h-5 text-slate-400" aria-hidden="true" />
                          </span>
                        </Listbox.Button>
                        <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <Listbox.Options className="absolute w-full py-1 mt-2 overflow-auto text-base bg-white dark:bg-slate-800 rounded-xl shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-50">
                            {months.map((month) => (
                              <Listbox.Option
                                key={month}
                                className={({ active }) =>
                                `cursor-pointer select-none relative py-2 pl-10 pr-4 transition-colors ${
                                  active ? 'text-blue-900 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-100' : 'text-slate-900 dark:text-slate-200'
                                  }`
                                }
                                value={month}
                              >
                                {({ selected }) => (
                                  <>
                                  <span className={`block truncate ${selected ? 'font-bold' : 'font-medium'}`}>{month}</span>
                                    {selected ? (
                                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600 dark:text-blue-400">
                                        <CheckIcon className="w-5 h-5" aria-hidden="true" />
                                      </span>
                                    ) : null}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    </Listbox>
                  </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Year</label>
                    <Listbox value={folderYear} onChange={(value) => setFolderYear(value)}>
                      <div className="relative mt-1">
                      <Listbox.Button className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all shadow-sm text-left">
                        <span className="block truncate">{folderYear}</span>
                        <span className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <SelectorIcon className="w-5 h-5 text-slate-400" aria-hidden="true" />
                          </span>
                        </Listbox.Button>
                        <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                        <Listbox.Options className="absolute w-full py-1 mt-2 overflow-auto text-base bg-white dark:bg-slate-800 rounded-xl shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-50">
                            {years.map((year) => (
                              <Listbox.Option
                                key={year}
                                className={({ active }) =>
                                `cursor-pointer select-none relative py-2 pl-10 pr-4 transition-colors ${
                                  active ? 'text-blue-900 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-100' : 'text-slate-900 dark:text-slate-200'
                                  }`
                                }
                                value={year}
                              >
                                {({ selected }) => (
                                  <>
                                  <span className={`block truncate ${selected ? 'font-bold' : 'font-medium'}`}>{year}</span>
                                    {selected ? (
                                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600 dark:text-blue-400">
                                        <CheckIcon className="w-5 h-5" aria-hidden="true" />
                                      </span>
                                    ) : null}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    </Listbox>
                  </div>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-extrabold tracking-tight dark:text-white text-slate-900">2. Configure Selected Files</h4>
              <span className="bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 text-xs font-bold px-2.5 py-1 rounded-full">{allSelectedFiles.length} File{allSelectedFiles.length !== 1 && 's'}</span>
            </div>
            <ul className="space-y-3">
              {allSelectedFiles.map((content, index) => (
                <li key={'File' + index} className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-sm gap-4">
                  <div className="flex items-center">
                    <div className="p-2 bg-slate-100 dark:bg-slate-700 rounded-lg mr-3 shrink-0">
                      <DocumentIcon className="w-5 h-5 text-slate-500 dark:text-slate-300" />
                    </div>
                    <span className="font-bold text-sm text-slate-900 dark:text-white truncate max-w-[200px] sm:max-w-xs">{content.name}</span>
                  </div>
                  <div className="flex items-center gap-4 sm:gap-6 bg-slate-50 dark:bg-slate-900/50 p-3 rounded-lg md:bg-transparent md:p-0">
                    <label className="flex items-center cursor-pointer">
                      <div className="relative">
                      <input
                          type="checkbox"
                          className="sr-only peer"
                        checked={isRestrictedClicked(content.name)}
                        onChange={() => onRestrictedClick(content.name)}
                      />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-blue-600"></div>
                    </div>
                      <span className="ml-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Restricted
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <div className="relative">
                      <input
                          type="checkbox"
                          className="sr-only peer"
                        checked={isSendEmailClicked(content.name)}
                        onChange={() => onSendEmailClick(content.name)}
                      />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-slate-600 peer-checked:bg-blue-600"></div>
                    </div>
                      <span className="ml-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Send Email
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={() => removeSelectedFile(content.name)}
                      className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors focus:outline-none"
                      title="Remove file"
                    >
                      <XIcon className="h-5 w-5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
            <button
              type="button"
              className="w-full inline-flex justify-center items-center rounded-xl px-6 py-3 bg-blue-600 text-base font-bold text-white hover:bg-blue-700 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={uploadFiles}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
              Upload {allSelectedFiles.length} File{allSelectedFiles.length !== 1 && 's'}
            </button>
            {!isEmpty(uploadMessage) && (
              <div className={`mt-4 p-3 rounded-lg text-sm text-center font-medium ${uploadMessage.includes('successfully') ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                {uploadMessage}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
